import './styles/base.css';
import './styles/sea.css';
import './styles/home.css';
import './styles/formations.css';
import './styles/breath.css';
import './styles/reveal.css';
import './styles/settings.css';
import './styles/reading.css';
import { askAI } from './ai/client';
import { buildCombinedPrompt, parseCombinedResponse } from './ai/prompts';
import { shuffledCards } from './data/cards';
import { getLang, initI18n, setLang } from './i18n';
import { mountSea } from './sea';
import type { DisposableScreen, Spread } from './types';
import { mountBreath } from './ui/breath';
import { mountFormations } from './ui/formations';
import { mountHome } from './ui/home';
import { mountReveal } from './ui/reveal';
import { mountSettingsPanel } from './ui/settings';

initI18n('zh');
mountSea(document.body);

// Settings panel mounts in both dev and prod. In prod it renders a
// minimal status-only view (no BYOK form). In dev it adds the
// developer-testing form (key inputs, save/clear, error line).
mountSettingsPanel(document.body);

// Global lang-toggle in <header> (index.html). Wires once at startup; the
// header is persistent across all screens, so no per-screen rebinding needed.
const syncLangActive = (): void => {
  document.querySelectorAll<HTMLElement>('header [data-lang-btn]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.langBtn === getLang());
  });
};
document.querySelectorAll<HTMLElement>('header [data-lang-btn]').forEach((btn) => {
  btn.addEventListener('click', () => {
    const v = btn.dataset.langBtn;
    if (v === 'en' || v === 'zh') setLang(v);
  });
});
window.addEventListener('linyuan:lang-change', syncLangActive);
syncLangActive();

const app = document.getElementById('app') as HTMLElement;

// Tracks the previous user input so the back-arrow on reveal can restore it
// when stepping back to formations.
let lastSubmit: { question: string; isGeneral: boolean } | null = null;
type ScreenName = 'home' | 'formations' | 'breath' | 'reveal';
// History "groups" collapse breath+reveal into one logical screen ('reading')
// so the Draw-again cycle doesn't grow the back stack — both transitions
// stay within the same group and use replaceState. Browser back from a
// reading group pops to formations regardless of which sub-screen the user
// was on.
type HistoryGroup = 'home' | 'formations' | 'reading';
const groupOf = (s: ScreenName): HistoryGroup => (s === 'breath' || s === 'reveal' ? 'reading' : s);

let currentScreen: ScreenName = 'home';
// True while we are servicing a popstate event — show() reads this to skip
// the history push (browser already handled the stack transition).
let fromPopstate = false;

const backArrow = document.getElementById('back-arrow') as HTMLElement;

backArrow.addEventListener('click', () => history.back());

// Drive popstate off e.state.group rather than the local currentScreen
// variable. This avoids "phantom" no-op back-presses where the UI didn't
// match the entry the browser popped to.
window.addEventListener('popstate', (e) => {
  fromPopstate = true;
  const target = (e.state as { group?: HistoryGroup } | null)?.group ?? 'home';
  if (target === 'home') {
    if (currentScreen !== 'home') goHome();
  } else if (target === 'formations') {
    if (currentScreen !== 'formations') {
      showFormations(lastSubmit ?? { question: '', isGeneral: true });
    }
  } else if (target === 'reading') {
    // Reveal can't be restored from history alone (the spread + cards live
    // only in JS state). Fall back to formations.
    if (currentScreen !== 'formations') {
      showFormations(lastSubmit ?? { question: '', isGeneral: true });
    }
  }
  fromPopstate = false;
});

function show(el: HTMLElement, screen: ScreenName): void {
  const prev = app.firstElementChild as DisposableScreen | null;
  prev?.dispose?.();
  app.replaceChildren(el);
  const prevGroup = groupOf(currentScreen);
  currentScreen = screen;
  backArrow.style.display = screen === 'home' ? 'none' : 'block';
  if (!fromPopstate) {
    const newGroup = groupOf(screen);
    if (screen === 'home') {
      // Home is the baseline — always replace. Stale entries below stay,
      // but popstate dispatch is e.state-driven so each back-press still
      // does something visible.
      history.replaceState({ group: 'home' }, '');
    } else if (newGroup !== prevGroup) {
      // Crossing a group boundary — push a new entry.
      history.pushState({ group: newGroup }, '');
    } else {
      // Same group (breath ↔ reveal during Draw-again) — replace, don't grow
      // the stack.
      history.replaceState({ group: newGroup }, '');
    }
  }
  window.scrollTo(0, 0);
}

function goHome(): void {
  // Clear lastSubmit so a back-press that lands on formations from the home
  // baseline doesn't render a stale question from a finished reading.
  lastSubmit = null;
  show(mountHome(showFormations), 'home');
}

function showFormations(result: { question: string; isGeneral: boolean }): void {
  lastSubmit = result;
  show(
    mountFormations(result, (spread: Spread) => {
      showBreathThenReveal(spread, result.question, result.isGeneral);
    }),
    'formations',
  );
}

/**
 * Show the breath screen for a min of 3s while the AI request is in flight,
 * then advance to reveal. Click anywhere to skip the timer (only effective
 * once the AI promise has settled). The AI fire + AbortController are owned
 * here so breath and reveal can share the same cached reading.
 */
function showBreathThenReveal(spread: Spread, question: string, isGeneral: boolean): void {
  const cards = shuffledCards().slice(0, spread.count);
  const ac = new AbortController();
  const cachedReading = askAI(
    buildCombinedPrompt({ spread, cards, question, isGeneral }),
    ac.signal,
  ).then((r) => parseCombinedResponse(r.text));
  // Swallow the unhandled-rejection warning; downstream attaches its own catches.
  cachedReading.catch(() => {
    /* handled by breath/reveal */
  });

  const ctx = { spread, cards, question, isGeneral, cachedReading };
  show(
    mountBreath(cachedReading, ac, spread, () => {
      const { element } = mountReveal(ctx, ac);
      show(element, 'reveal');
    }),
    'breath',
  );
}

goHome();

window.addEventListener('linyuan:nav', (e) => {
  if (e.detail.to === 'home') {
    goHome();
  } else if (e.detail.to === 'reveal-reset') {
    // Draw-again redraws cards for the SAME question — preserve the user's
    // original question and isGeneral flag so the AI prompt has full context.
    const { question, isGeneral } = lastSubmit ?? { question: '', isGeneral: true };
    showBreathThenReveal(e.detail.spread, question, isGeneral);
  }
});
